import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { debounce, isEmpty } from 'lodash';
import classnames from 'classnames';
import { Stack } from '@mui/material';
import { ResourcePopoverMenu } from 'new/elements/ResourcePopoverMenu/ResourcePopoverMenu';
import HeaderMetaConfiguration from 'js/components/destination/HeaderMetaConfiguration/HeaderMetaConfiguration';
import DataSourceDetails from 'js/components/data_sources/add_ds_form/DataSourceDetails/DataSourceDetails';
import MonitoringInterval from 'js/components/data_sources/add_ds_form/MonitoringInterval/MonitoringInterval';
import JSONUtils from 'js/lib/JSONUtils';
import { tryCatchAsync } from 'new/utils/TryCatch';
import { buildAutoSelectSql } from 'new/utils/sqlAutoQuery';
import {
  SchemaTreePanel,
  type SchemaColumn,
  type SchemaTable,
} from 'js/components/data_sources/add_ds_form/database/redesign/SchemaTreePanel';
import { SqlEditorPanel } from 'js/components/data_sources/add_ds_form/database/redesign/SqlEditorPanel';
import {
  SourceTablePreview,
  type DatabaseSampleData,
} from 'js/components/data_sources/add_ds_form/database/redesign/SourceTablePreview';
import { SUPPORT_KEY_SOURCE_FORM_DB } from 'js/constants/supportPageKeys';
import { EmptyView } from 'new/elements/EmptyView/EmptyView';
import { SectionNav, useSectionNav } from 'new/elements/SectionNav';
import { useSqlRunnerDatabaseTree } from './useSqlRunnerDatabaseTree';
import { UiMode } from 'new/constants/uiMode';
import 'js/components/data_sources/add_ds_form/database/DatabaseConfigurator.scss';
import 'js/components/data_sources/add_ds_form/SelectBucket.scss';
import 'js/components/data_sources/add_ds_form/ConfigureAPI.scss';
import 'js/components/data_sources/add_ds_form/database/redesign/SourceTableRedesign.scss';
import styles from './SqlRunnerConfiguration.module.scss';

export interface SqlRunnerConfigurationProps {
  selectedCredentialId: string | null;
  selectedConnection: string | null;
  connectionMetas: Record<string, unknown> | null;
  sourceConfigFields: Record<string, unknown[]>;
  sourceConfig: Record<string, unknown>;
  sourceBeingEdited: Record<string, unknown> | null;
  onSourceConfigChanged: (config: Record<string, unknown>) => void;
  updateFormData: (payload: Record<string, unknown>) => void;
  onRequestProbeTree: (expand?: boolean, node?: unknown) => void;
  onRequestInitialDBProbeTree: (payload: { name: string; parent: string }) => void;
  onGetProbeSample: (() => void) | null;
  probeData: unknown[] | null;
  probeDataFlat: Record<string, unknown> | null;
  probeSample: unknown;
  probeError: unknown;
  probeTreeLastUpdatedAt: number | null;
  isLoadingProbeData: boolean;
  isLoadingProbeSample: boolean;
  selectedDatabase: string | null;
  selectedTable: { id: string; name: string; columns?: unknown[] } | null;
  parentDataSet: unknown | null;
  parentDataSetSamples: unknown | null;
  onTableSelected: (
    table: { id: string; name: string; columns?: unknown[] } | null,
    databaseId?: string,
  ) => void;
  setContextualSupport: (key: string) => void;
  unsetContextualSupport: (key: string) => void;
  sourceName: string;
  sourceDescription: string;
  selectedBucket: string | null;
  sourceConfigFieldsList?: unknown[];
  onInputChange: (args: { property: string; value: unknown }) => void;
  selectedConnectionDetails: unknown | null;
  visibleFieldData: Record<string, unknown>;
  goBack: () => void;
  onSaveAdvancedMode: (config: Record<string, unknown>) => void;
  onError?: (error: unknown) => void;
  queryModeOnly?: boolean;
  currentOrg?: { id: string };
  onEditedSamplesChanged?: (editedSamples: unknown) => void;
  nextFormStage?: () => void;
}

const MAX_PINNED_TABLES = 5;

function getResourceConfig(
  sourceConfig: Record<string, unknown>,
  sourceName: string,
  sourceDescription: string,
) {
  const clonedSourceConfig = structuredClone(sourceConfig);
  const name = sourceName || (clonedSourceConfig.name as string) || '';
  const description = sourceDescription || (clonedSourceConfig.description as string) || '';

  delete clonedSourceConfig.name;
  delete clonedSourceConfig.description;
  delete clonedSourceConfig.sourceName;
  delete clonedSourceConfig.sourceDescription;

  return {
    name,
    description,
    source_config: clonedSourceConfig,
  };
}

export function SqlRunnerConfiguration(props: SqlRunnerConfigurationProps) {
  const {
    sourceName,
    sourceDescription,
    onInputChange,
    selectedConnection,
    parentDataSet,
    goBack,
    onSaveAdvancedMode,
    onError,
    sourceConfig,
    sourceConfigFieldsList,
    selectedConnectionDetails,
    nextFormStage,
    selectedCredentialId,
    probeData,
    probeDataFlat,
    probeSample,
    selectedTable,
    selectedDatabase,
    onTableSelected,
    onSourceConfigChanged,
    onRequestProbeTree,
    onRequestInitialDBProbeTree,
    onGetProbeSample,
    setContextualSupport,
    unsetContextualSupport,
    isLoadingProbeData,
    isLoadingProbeSample,
    probeError,
    connectionMetas,
    visibleFieldData,
    sourceBeingEdited,
    sourceConfigFields,
  } = props;

  const [pinnedTableIds, setPinnedTableIds] = useState<string[]>([]);
  const [localSelectedTableId, setLocalSelectedTableId] = useState<string | null>(null);
  // True once the user has manually edited the query; reset when a tree table is selected.
  const [isCustomQuery, setIsCustomQuery] = useState(() => !!sourceConfig?.query);

  const {
    databasesArray,
    tables,
    tablesForDatabase,
    selectedDatabaseId,
    selectDatabase,
    expandTable,
    effectiveSamplesData,
  } = useSqlRunnerDatabaseTree({
    probeData,
    probeDataFlat,
    probeSample,
    sourceConfig,
    sourceBeingEdited,
    selectedDatabase,
    selectedTable,
    selectedCredentialId,
    onSourceConfigChanged,
    onTableSelected,
    onRequestProbeTree,
  });

  const onRequestTree = useCallback(() => {
    if (sourceConfig?.database && sourceConfig?.table) {
      onRequestInitialDBProbeTree({
        name: sourceConfig.table as string,
        parent: sourceConfig.database as string,
      });
    } else {
      onRequestProbeTree();
    }
  }, [sourceConfig, onRequestInitialDBProbeTree, onRequestProbeTree]);

  // Ref so the effect always calls the latest onRequestTree without listing it
  // as a dep — onRequestTree depends on sourceConfig (a new object each render)
  // which would cause an infinite probe loop if included directly.
  const onRequestTreeRef = useRef(onRequestTree);
  onRequestTreeRef.current = onRequestTree;

  useEffect(() => {
    if (selectedCredentialId) {
      onRequestTreeRef.current();
    }
    setContextualSupport(SUPPORT_KEY_SOURCE_FORM_DB);
    return () => unsetContextualSupport(SUPPORT_KEY_SOURCE_FORM_DB);
  }, [selectedCredentialId]);

  // Keep loading until databasesArray is populated — probeData arrives before
  // initializeState (a useEffect) processes it, causing a one-render gap where
  // databasesArray is empty even though data exists.
  const isLoadingTree =
    (isEmpty(probeData) && isLoadingProbeData) ||
    (!isEmpty(probeData) && databasesArray.length === 0);
  const meta = connectionMetas?.[selectedConnection];

  const frequencyField = useMemo(() => {
    const allFields = (sourceConfigFieldsList ?? sourceConfigFields?.[selectedConnection]) as
      | { name?: string }[]
      | undefined;

    return allFields?.find(
      (f) => f?.name === 'parameters.script.start.cron' || f?.name === 'start.cron',
    ) as React.ComponentProps<typeof MonitoringInterval>['field'] | undefined;
  }, [sourceConfigFieldsList, sourceConfigFields, selectedConnection]);

  const frequencyValue = useMemo(() => {
    const key = frequencyField?.name;
    return (sourceConfig?.[key] ?? visibleFieldData?.[key]) as string | undefined;
  }, [sourceConfig, visibleFieldData]);

  const query = (sourceConfig?.query as string) ?? '';
  const shouldDisableTest = isEmpty(query);

  const onFetchSampleData = shouldDisableTest || !onGetProbeSample ? null : onGetProbeSample;

  const hasProhibitedQueryError = useMemo(() => {
    const samples = effectiveSamplesData as { statusCode?: number } | null;
    return samples?.statusCode === 403;
  }, [effectiveSamplesData]);

  const queryErrorHint = hasProhibitedQueryError ? (
    <>
      <strong>This query can&apos;t be previewed here.</strong> Preview supports read-only SELECT
      queries — other statements will still run normally when your automation executes.
    </>
  ) : undefined;

  const noop = useCallback(() => {}, []);

  const onTogglePin = useCallback((tableId: string) => {
    setPinnedTableIds((prev) =>
      prev.includes(tableId)
        ? prev.filter((id) => id !== tableId)
        : [tableId, ...prev].slice(0, MAX_PINNED_TABLES),
    );
  }, []);

  const sourceConfigRef = useRef(sourceConfig);
  sourceConfigRef.current = sourceConfig;
  const tablesRef = useRef(tables);
  tablesRef.current = tables;
  const localSelectedTableIdRef = useRef(localSelectedTableId);
  localSelectedTableIdRef.current = localSelectedTableId;

  const buildAutoSql = useCallback(
    (tableId: string | null): string => {
      const table = tableId
        ? (tablesRef.current[tableId] as { name?: string; database?: string } | undefined)
        : undefined;
      return buildAutoSelectSql(table?.name, table?.database, selectedConnection);
    },
    [selectedConnection],
  );

  // In query-only mode we browse the tree for schema inspection but must NOT
  // write `sourceConfig.table` — that would corrupt the samples payload.
  // Instead, probe columns (for the tooltip), update local highlight, and
  // populate the editor with the auto-generated SELECT for the chosen table.
  const onSelectTableInTree = useCallback(
    (tableId: string) => {
      setLocalSelectedTableId(tableId);
      setIsCustomQuery(false);
      expandTable(tableId);
      const autoSql = buildAutoSql(tableId);
      if (autoSql) {
        onSourceConfigChanged({ ...sourceConfigRef.current, query: autoSql });
      }
    },
    [expandTable, buildAutoSql, onSourceConfigChanged],
  );

  const onResetToTable = useCallback(() => {
    setIsCustomQuery(false);
    const autoSql = buildAutoSql(localSelectedTableIdRef.current);
    if (autoSql) {
      onSourceConfigChanged({ ...sourceConfigRef.current, query: autoSql });
    }
  }, [buildAutoSql, onSourceConfigChanged]);

  const handleLoadableMonacoEditorChange = useMemo(
    () =>
      debounce((queryValue: string) => {
        if (queryValue !== buildAutoSql(localSelectedTableIdRef.current)) {
          setIsCustomQuery(true);
        }
        onSourceConfigChanged({
          ...sourceConfigRef.current,
          query: queryValue,
        });
      }, 200),
    [onSourceConfigChanged, buildAutoSql],
  );
  useEffect(
    () => () => handleLoadableMonacoEditorChange.cancel(),
    [handleLoadableMonacoEditorChange],
  );

  const handleLoadableMonacoEditorMount = useCallback((_editor: unknown, monaco: unknown) => {
    const m = monaco as {
      languages: {
        CompletionItemKind: { Class: number; Field: number };
        registerCompletionItemProvider: (
          lang: string,
          provider: {
            provideCompletionItems: (
              model: unknown,
              position: unknown,
            ) => { suggestions: unknown[] };
          },
        ) => { dispose: () => void };
      };
    };
    const { CompletionItemKind, registerCompletionItemProvider } = m.languages;

    return [
      registerCompletionItemProvider('sql', {
        provideCompletionItems(model: unknown, position: unknown) {
          const pos = position as { lineNumber: number; column: number };
          const mod = model as {
            getWordUntilPosition: (p: unknown) => {
              startColumn: number;
              endColumn: number;
            };
          };
          const word = mod.getWordUntilPosition(position);
          const range = {
            startLineNumber: pos.lineNumber,
            endLineNumber: pos.lineNumber,
            startColumn: word.startColumn,
            endColumn: word.endColumn,
          };

          const suggestions: {
            label: string;
            kind: number;
            insertText: string;
            range: typeof range;
          }[] = [];

          Object.values(tablesRef.current).forEach((t) => {
            if (t?.name) {
              suggestions.push({
                label: t.name,
                kind: CompletionItemKind.Class,
                insertText: t.name,
                range,
              });
            }
          });

          Object.values(tablesRef.current).forEach((t) => {
            (t?.columns ?? []).forEach((col: unknown) => {
              const c = col as { name?: string };
              if (c?.name) {
                suggestions.push({
                  label: c.name,
                  kind: CompletionItemKind.Field,
                  insertText: c.name,
                  range,
                });
              }
            });
          });

          return { suggestions };
        },
      }),
    ];
  }, []);

  const dispatchAdvancedModeSave = useCallback(
    async (sourceConfigString: string) => {
      if (!onError) return;

      if (!JSONUtils.isJSON(sourceConfigString)) {
        onError('The provided JSON is invalid. Please check the syntax and try again.');
        return;
      }

      const parsedConfig = JSON.parse(sourceConfigString) as {
        name?: string;
        description?: string;
        source_config?: Record<string, unknown>;
      };
      const { name, description, source_config } = parsedConfig;

      const data: Record<string, unknown> = {
        name,
        description,
        ...source_config,
        'ui.mode': UiMode.CODE,
      };

      const { error } = await tryCatchAsync(Promise.resolve(onSaveAdvancedMode(data)));
      if (error) {
        onError(error.message);
        return;
      }

      nextFormStage?.();
    },
    [onError, onSaveAdvancedMode, nextFormStage],
  );

  const onSwitchUiMode = useCallback(() => {
    onSourceConfigChanged({
      ...sourceConfig,
      'ui.mode': UiMode.VIEW,
    });
  }, [sourceConfig, onSourceConfigChanged]);

  const resourceConfig = getResourceConfig(sourceConfig, sourceName, sourceDescription);

  const handleMonitoringConfigChanged = useCallback(
    (data: Record<string, unknown>, fieldName: string, value: unknown) => {
      onSourceConfigChanged({ ...data, [fieldName]: value });
    },
    [onSourceConfigChanged],
  );

  const connectionName =
    (meta as { displayName?: string })?.displayName ?? selectedConnection ?? undefined;

  const sections = useMemo(
    () => [
      { id: 'source', label: 'Source Table' },
      ...(frequencyField ? [{ id: 'section-scheduling', label: 'Schedule Ingestion' }] : []),
    ],
    [frequencyField],
  );

  const { active, sectionRefs, scrollToSec } = useSectionNav(sections);

  return (
    <div className={styles.pageLayout}>
      <div className={styles.stickyHeader}>
        <DataSourceDetails
          name={sourceName}
          description={sourceDescription}
          onInputChange={onInputChange}
          selectedConnectionDetails={selectedConnectionDetails}
          leftView={
            <HeaderMetaConfiguration
              enableInputFields={false}
              isDestination={false}
              onChange={onInputChange}
              connection={selectedConnection}
              sourceOrDestinationChipChildren={
                parentDataSet ? null : (
                  <ResourcePopoverMenu
                    resourceConfig={resourceConfig}
                    connectorType={selectedConnection ?? undefined}
                    jsonConfigOnSave={dispatchAdvancedModeSave}
                    jsonConfigOnClose={goBack}
                    onSwitchUiMode={onSwitchUiMode}
                    resourcePersisted={!!sourceBeingEdited?.id}
                    disabledActions={['view-json-config']}
                  />
                )
              }
            />
          }
        />
      </div>
      <div className={styles.mainContent}>
        <div className={styles.formRow}>
          <SectionNav sections={sections} active={active} onGo={scrollToSec} />
          <div className={styles.sectionsContainer}>
            <div
              id="source"
              ref={(el) => {
                sectionRefs.current['source'] = el;
              }}
            >
              {probeError ? (
                <Stack padding="40px">
                  <EmptyView
                    title="There was an error loading resources"
                    actionTitle="Try Again"
                    actionProps={{ onClick: onRequestTree }}
                  />
                </Stack>
              ) : (
                <div className="db-source-redesign">
                  <SchemaTreePanel
                    databases={databasesArray}
                    tablesForDatabase={tablesForDatabase}
                    tables={tables as Record<string, SchemaTable>}
                    selectedDatabaseId={selectedDatabaseId}
                    selectedTableId={localSelectedTableId}
                    pinnedTableIds={pinnedTableIds}
                    onSelectDatabase={selectDatabase}
                    onSelectTable={onSelectTableInTree}
                    onTogglePin={onTogglePin}
                    onProbeTable={expandTable}
                    loading={isLoadingTree}
                    connectionName={connectionName}
                  />
                  <div className="db-workspace">
                    <div className="db-workspace-body">
                      <div className="db-workspace-editor">
                        <SqlEditorPanel
                          value={query}
                          isCustom={isCustomQuery}
                          selectedTableLabel={
                            localSelectedTableId
                              ? (tables[localSelectedTableId]?.name ?? undefined)
                              : undefined
                          }
                          canRun={!!onFetchSampleData}
                          running={isLoadingProbeSample}
                          onChange={handleLoadableMonacoEditorChange}
                          onRun={onFetchSampleData ?? noop}
                          onResetToTable={onResetToTable}
                          onMount={handleLoadableMonacoEditorMount}
                        />
                      </div>
                      <div className="db-workspace-preview">
                        <SourceTablePreview
                          columns={(selectedTable?.columns ?? []) as SchemaColumn[]}
                          samples={effectiveSamplesData as DatabaseSampleData}
                          loading={isLoadingProbeSample}
                          tableLabel={selectedTable?.name}
                          canFetch={!!onFetchSampleData}
                          onFetch={onFetchSampleData ?? noop}
                          mode="Query"
                          errorHint={queryErrorHint}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {frequencyField && (
              <div
                id="section-scheduling"
                ref={(el) => {
                  sectionRefs.current['section-scheduling'] = el;
                }}
                className={styles.schedulingSection}
              >
                <MonitoringInterval
                  field={frequencyField}
                  source={sourceBeingEdited}
                  config={{
                    ...visibleFieldData,
                    ...(frequencyField.name ? { [frequencyField.name]: frequencyValue } : {}),
                  }}
                  monitorIntervalContainerClassname={classnames({
                    scheduling: true,
                    'scheduling-focused': active === 'section-scheduling',
                  })}
                  onConfigChanged={handleMonitoringConfigChanged}
                />
              </div>
            )}
            <div className={styles.scrollSpacer} />
          </div>
        </div>
      </div>
    </div>
  );
}
